import { useState, useCallback } from 'react';
import { CompradorDrillDownData } from '@/types/drillDown';

type DrillLevel = 'list' | 'detail';

interface UseCompradorDrillDownProps {
  spedFiscalId: number | null | undefined;
}

interface UseCompradorDrillDownReturn {
  drillLevel: DrillLevel;
  selectedComprador: string | null;
  drillDownData: CompradorDrillDownData | null;
  loading: boolean;
  error: string | null;
  handleBarClick: (clickData: any) => Promise<void>;
  handleDrillUp: () => void;
  retry: () => Promise<void>;
  // compat DrillDownModal
  isModalOpen: boolean;
  data: CompradorDrillDownData | null;
  selectedItem: { nome: string } | null;
  openModal: (c: { nome: string; cod_part?: string } | string) => Promise<void>;
  closeModal: () => void;
}

/**
 * Hook personalizado para gerenciar o drilldown de compradores
 * Segue os princípios SOLID:
 * - SRP: Responsabilidade única de gerenciar estado e lógica do drilldown de compradores
 * - OCP: Extensível sem modificar código existente
 * - DIP: Dependências injetadas via props
 * - ISP: Interface específica para dados de comprador
 * - Baixo acoplamento: Independente de outros módulos
 */
export const useCompradorDrillDown = ({ 
  spedFiscalId 
}: UseCompradorDrillDownProps): UseCompradorDrillDownReturn => {
  const [drillLevel, setDrillLevel] = useState<DrillLevel>('list');
  const [selectedComprador, setSelectedComprador] = useState<string | null>(null);
  const [drillDownData, setDrillDownData] = useState<CompradorDrillDownData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Busca dados detalhados do comprador no backend
   * @param compradorName - Nome do comprador selecionado
   */
  const fetchDrillDownData = useCallback(async (comprador: { nome: string; cod_part?: string } | string) => {
    // Validação do ID fiscal
    if (spedFiscalId === null || spedFiscalId === undefined || spedFiscalId <= 0) {
      setError('ID fiscal inválido');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const compradorName = typeof comprador === 'string' ? comprador : comprador.nome;
      const codPart = typeof comprador === 'string' ? undefined : comprador.cod_part;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}sped-fiscal/comprador-detalhes-drill-down/${spedFiscalId}/${encodeURIComponent(compradorName)}${codPart ? `?cod_part=${encodeURIComponent(codPart)}` : ''}`
      );

      if (!response.ok) {
        throw new Error(`Erro HTTP! status: ${response.status}`);
      }

      const data: CompradorDrillDownData = await response.json();

      // Validação dos dados retornados
      if (data && typeof data === 'object') {
        setDrillDownData(data);
        setDrillLevel('detail');
      } else {
        throw new Error('Dados inválidos retornados do servidor');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      console.error('Erro ao buscar detalhes do comprador:', err);
    } finally {
      setLoading(false);
    }
  }, [spedFiscalId]);

  /**
   * Handler para clique na barra do gráfico
   * @param clickData - Dados do clique contendo informações do comprador
   */
  const handleBarClick = useCallback(async (clickData: any) => {
    if (spedFiscalId === null || spedFiscalId === undefined || spedFiscalId <= 0) {
      setError('ID fiscal inválido');
      return;
    }

    const compradorName = clickData.indexValue as string;
    const codPart = (clickData as any).cod_part;
    setSelectedComprador(compradorName);
    await fetchDrillDownData({ nome: compradorName, cod_part: codPart });
  }, [spedFiscalId, fetchDrillDownData]);

  /**
   * Volta para o nível de lista (gráfico principal)
   */
  const handleDrillUp = useCallback(() => {
    setDrillLevel('list');
    setSelectedComprador(null);
    setDrillDownData(null);
    setError(null);
  }, []);

  /**
   * Tenta buscar os dados novamente em caso de erro
   */
  const retry = useCallback(async () => {
    if (selectedComprador) {
      await fetchDrillDownData(selectedComprador);
    }
  }, [selectedComprador, fetchDrillDownData]);

  return {
    drillLevel,
    selectedComprador,
    drillDownData,
    loading,
    error,
    handleBarClick,
    handleDrillUp,
    retry,
    isModalOpen: drillLevel === 'detail',
    data: drillDownData,
    selectedItem: selectedComprador ? { nome: selectedComprador } : null,
    openModal: (c: { nome: string; cod_part?: string } | string) =>
      fetchDrillDownData(typeof c === 'string' ? { nome: c } : c),
    closeModal: handleDrillUp
  };
}; 